import { useDeepCompareEffect, useSyncedRef } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import { useEffect, useMemo, useState } from "react";
import { graphql, readInlineData } from "react-relay";

import { getGAIdFromCookie } from "shared/components/tracking/google-analytics/apis";
import { useCurrentPageType } from "shared/hooks";
import { accountAtom } from "shared/lib/account";
import { PageType } from "shared/lib/page-type";

import { isInfiniteScrollContentAtom } from "scmp-app/components/article/article-list/atoms";
import { appAtom } from "scmp-app/lib/app/atoms";
import { currentArticleAtom } from "scmp-app/lib/current-item";
import type { hooksUsePageTargetingArticle$key } from "scmp-app/queries/__generated__/hooksUsePageTargetingArticle.graphql";

export const useDefineSlot = (
  adUnitPath: string,
  sizes: googletag.GeneralSize,
  targeting: Record<string, string | string[]>,
  options?: {
    divId?: string;
    sizeMappingBuilder?: (builder: googletag.SizeMappingBuilder) => googletag.SizeMappingArray;
  },
) => {
  const [slot, setSlot] = useState<googletag.Slot | null>(null);

  useDeepCompareEffect(() => {
    let slot: OrUndefined<googletag.Slot>;
    googletag.cmd.push(() => {
      slot = options?.divId
        ? googletag.defineSlot(adUnitPath, sizes, options.divId)?.addService(googletag.pubads())
        : googletag.defineSlot(adUnitPath, sizes)?.addService(googletag.pubads());
      if (!slot) return;

      if (options?.sizeMappingBuilder) {
        slot.defineSizeMapping(options.sizeMappingBuilder(googletag.sizeMapping()));
      }
      slot?.setConfig({ targeting });
      setSlot(slot);
    });

    return () => {
      googletag.cmd.push(() => {
        if (!slot) return;
        googletag.destroySlots([slot]);
        setSlot(null);
      });
    };
  }, [adUnitPath, options, sizes, targeting]);

  return slot;
};

export const useRenderSlotInfo = (slot: googletag.Slot | null) => {
  const [state, setState] = useState<null | RenderedSlotInfo>(null);
  const latestSlot = useSyncedRef(slot);

  useEffect(() => {
    const listener: (event: googletag.events.SlotRenderEndedEvent) => void = event => {
      if (!latestSlot.current) {
        setState(null);
        return;
      }
      if (event.slot !== latestSlot.current) return;
      const { slot: _omitted, ...remaining } = event;
      setState({ ...remaining });
    };

    googletag.cmd.push(() => {
      googletag.pubads().addEventListener("slotRenderEnded", listener);
    });

    return () => {
      googletag.cmd.push(() => {
        googletag.pubads().removeEventListener("slotRenderEnded", listener);
      });
    };
  }, [latestSlot]);

  return state;
};

export type RenderedSlotInfo = Omit<googletag.events.SlotRenderEndedEvent, "slot">;

export const usePageTargeting = () => {
  const { user } = useAtomValue(accountAtom);
  const { navigatorInfo } = useAtomValue(appAtom);
  const isInfiniteScrollContent = useAtomValue(isInfiniteScrollContentAtom);
  const currentPageType = useCurrentPageType();

  const pageTypeTargeting = useMemo<PageType | undefined>(() => {
    switch (currentPageType) {
      case PageType.Article:
      case PageType.Homepage:
      case PageType.Section:
      case PageType.SubSection:
      case PageType.Topic:
        return currentPageType;
      default:
        return;
    }
  }, [currentPageType]);

  const currentArticle = useAtomValue(currentArticleAtom);
  const article = readInlineData<hooksUsePageTargetingArticle$key>(
    graphql`
      fragment hooksUsePageTargetingArticle on Article @inline {
        sectionsV2 {
          value {
            ... on Section {
              name
            }
          }
        }
        firstTopic {
          name
        }
      }
    `,
    currentArticle,
  );

  useEffect(() => {
    googletag.cmd.push(() => {
      const searchParameters = new URL(location.href).searchParams;
      const cid = searchParameters.get("cid");
      if (cid) {
        googletag.setConfig({ targeting: { cid } });
      }

      if (navigatorInfo) {
        googletag.setConfig({
          targeting: {
            ad_bot: String(navigatorInfo?.isBot ?? false),
            bot_score: navigatorInfo?.botScoreGroup ?? "0",
          },
        });
      }

      if (pageTypeTargeting) {
        googletag.setConfig({ targeting: { pagetype: pageTypeTargeting } });
      }

      if (user?.uuid) {
        googletag.setConfig({ targeting: { uuid: user.uuid } });
      }

      const gaClientId = getGAIdFromCookie();
      const publisherProvidedId = user?.uuid ?? gaClientId;
      if (publisherProvidedId) {
        googletag.pubads().setPublisherProvidedId(publisherProvidedId);
      }

      if (gaClientId) {
        googletag.setConfig({ targeting: { ga_id: gaClientId } });
      }
    });
  }, [navigatorInfo, pageTypeTargeting, user?.uuid]);

  useEffect(() => {
    if (currentPageType !== PageType.Article) return;

    const category = first(article?.sectionsV2)?.value?.map(item => item?.name ?? "");
    const firstTopic = article?.firstTopic?.name;

    googletag.cmd.push(() => {
      if (category && category.every(item => item !== "")) {
        googletag.setConfig({ targeting: { category } });
      }
      if (firstTopic) {
        googletag.setConfig({ targeting: { topic: [firstTopic] } });
      }
    });
  }, [article?.firstTopic?.name, article?.sectionsV2, currentPageType]);

  // Separate effect for infinite scroll to update when it changes
  useEffect(() => {
    googletag.cmd.push(() => {
      googletag.setConfig({
        targeting: { infinite_scroll: String(isInfiniteScrollContent ?? false) },
      });
    });
  }, [isInfiniteScrollContent]);
};
